import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RolePicker } from "@/components/UsersManager";
import { KeyRound, ShieldCheck, Unlink, UserPlus, Link2, Loader2, Mail, LogOut } from "lucide-react";

const CRIMSON = "#bf3b4f";

export function LoginAccessCard({ employee, suggestedEmail }) {
  const [status, setStatus] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [mode, setMode] = useState(null);      // "create" | "link" | null
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState([]);
  const [sw, setSw] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => api.hrStaffLoginStatus(employee).then((d) => { setStatus(d); setEmail(d.suggested_email || suggestedEmail || ""); }).catch(() => {});
  useEffect(() => { load(); api.authRolesCatalog().then((d) => setCatalog(d.roles || [])).catch(() => {}); }, [employee]);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); setMode(null); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } finally { setBusy(false); } };
  const user = status?.user;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mt-4" data-testid="login-access-card">
      <div className="flex items-center gap-2 mb-2"><KeyRound className="h-4 w-4" style={{ color: CRIMSON }} /><h3 className="text-sm font-semibold font-brand text-slate-800">Login access</h3></div>
      {user ? (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{user.email}</span>
            <Badge className="border bg-sky-50 text-sky-700 border-sky-200"><Link2 className="h-3 w-3 mr-1" />Linked</Badge>
            {user.must_reset_password && <Badge className="border bg-amber-50 text-amber-700 border-amber-200">Awaiting password</Badge>}
            {user.twofa_method && <Badge className="border bg-emerald-50 text-emerald-700 border-emerald-200"><ShieldCheck className="h-3 w-3 mr-1" />2FA</Badge>}
          </div>
          <div className="mt-3">
            <Label className="text-[11px] text-slate-500 mb-1.5 block">Roles &amp; access</Label>
            <RolePicker catalog={catalog} value={user.frappe_roles || []} onChange={(v) => act(() => api.authSetUserRoles(user.email, v), "Roles updated.")} />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => act(() => api.authResetUserPw(user.email), "Set-password link sent.")} data-testid="la-reset-pw"><KeyRound className="h-4 w-4 mr-1.5" />Send set-password link</Button>
            <Button size="sm" variant="outline" onClick={() => act(() => api.authResendWelcome(user.email), "Welcome email sent.")} data-testid="la-resend-welcome"><Mail className="h-4 w-4 mr-1.5" />Resend welcome</Button>
            <Button size="sm" variant="outline" onClick={() => act(() => api.authResetUser2fa(user.email), "2FA reset.")} data-testid="la-reset-2fa"><ShieldCheck className="h-4 w-4 mr-1.5" />Reset 2FA</Button>
            <Button size="sm" variant="outline" onClick={() => window.confirm(`Sign ${user.email} out on every device now?`) && act(() => api.authSignOutUser(user.email), "Signed out on every device.")} data-testid="la-sign-out"><LogOut className="h-4 w-4 mr-1.5" />Sign out everywhere</Button>
            <Button size="sm" variant="outline" onClick={() => window.confirm("Unlink this login from the HR profile?") && act(() => api.authUnlinkUser(user.email), "Unlinked.")} data-testid="la-unlink"><Unlink className="h-4 w-4 mr-1.5" />Unlink</Button>
          </div>
        </>
      ) : mode === null ? (
        <>
          <p className="text-sm text-slate-500">No login is linked to this staff member yet.</p>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={() => setMode("create")} data-testid="la-create"><UserPlus className="h-4 w-4 mr-1.5" />Create login</Button>
            <Button size="sm" variant="outline" onClick={() => setMode("link")} data-testid="la-link"><Link2 className="h-4 w-4 mr-1.5" />Link existing user</Button>
          </div>
        </>
      ) : mode === "create" ? (
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] text-slate-500">Login email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@prydessupport.com" className="mt-1 h-9 text-sm" data-testid="la-email" />
          </div>
          <label className="flex items-center gap-2 text-[12px] text-slate-600"><Switch checked={sw} onCheckedChange={setSw} data-testid="la-sw" /> Support Worker (mobile app)</label>
          <div>
            <Label className="text-[11px] text-slate-500 mb-1.5 block">Additional roles (optional)</Label>
            <RolePicker catalog={catalog.filter((c) => c.value !== "Support Worker")} value={roles} onChange={setRoles} />
          </div>
          <p className="text-[11px] text-slate-400">They'll set their own password via "Forgot password" (or use Send set-password link after creating).</p>
          <div className="flex items-center gap-2">
            <Button size="sm" style={{ backgroundColor: CRIMSON }} disabled={busy || !email} onClick={() => act(() => api.hrCreateLogin(employee, { email, roles, support_worker: sw }), "Login created & linked.")} data-testid="la-create-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & link"}</Button>
            <Button size="sm" variant="outline" onClick={() => setMode(null)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] text-slate-500">Existing user email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="existing.user@prydessupport.com" className="mt-1 h-9 text-sm" data-testid="la-link-email" />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" style={{ backgroundColor: CRIMSON }} disabled={busy || !email} onClick={() => act(() => api.hrLinkLogin(employee, email), "Linked existing user.")} data-testid="la-link-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Link user"}</Button>
            <Button size="sm" variant="outline" onClick={() => setMode(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
